/**
 * V2AnimationsSection - Theme editor section for the motion scheme
 * (schema 2.17 animation, theme plane).
 *
 * Preset-first UX: 5 curated scheme cards fill concrete
 * `ThemeV2AnimationSettings` values; Advanced exposes the underlying
 * transition/entrance fields. Divergent manual edits surface as an implicit
 * "Custom" state (derived — never trusted from the stored `motionPreset`).
 *
 * Emits theme-level paths (`animationSettings.*`) through the standard
 * onChange(path, value) contract; materialization onto the template
 * FlowDocument is owned by ThemeV2EditorView's cascade branch.
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Subpath import: the @modules/ui-components root barrel is not jest-safe (it transitively
// reaches firebase-core's `import.meta.env`); runtime resolves to the same module.
import { SAccordion } from '@modules/ui-components/dialogs/SAccordion';
import {
  DEFAULT_THEME_MOTION_PRESET_ID,
  THEME_DIRECTIONAL_TRANSITION_TYPES,
  THEME_EASING_OPTIONS,
  THEME_MOTION_PRESETS,
  THEME_PAYWALL_CALM_OVERRIDE,
  THEME_TRANSITION_DIRECTION_OPTIONS,
  THEME_TRANSITION_TYPE_OPTIONS,
  expandMotionPreset,
  resolveMotionPresetId,
} from '@modules/themes/animation/themeAnimationPresets';
import { V2_ENTRANCE_PRESET_OPTIONS } from '@modules/reactFlow/config/v2AnimationPresetOptions';
import type { ThemeV2AnimationSettings } from '../../types/themesV2.types';

const SCHEME_ICONS: Record<string, string> = {
  none: '⊘',
  subtle: '◦',
  smooth: '≋',
  dynamic: '≫',
  playful: '✦',
};

const TRANSITION_DURATION_MIN = 50;
const TRANSITION_DURATION_MAX = 10000;
const STAGGER_MIN = 0;
const STAGGER_MAX = 1000;
const TRANSITION_DELAY_MIN = 0;
const TRANSITION_DELAY_MAX = 10000;

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export interface V2AnimationsSectionProps {
  /** Current animation settings (absent = theme does not manage motion) */
  animationSettings?: ThemeV2AnimationSettings;
  /** Callback when any setting changes - receives theme-level path and value */
  onChange: (path: string, value: unknown) => void;
  /** Whether section is disabled */
  disabled?: boolean;
  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
  /** Optional transition demo strip rendered at the section bottom */
  transitionDemo?: React.ReactNode;
}

export const V2AnimationsSection: React.FC<V2AnimationsSectionProps> = ({
  animationSettings,
  onChange,
  disabled = false,
  defaultExpanded = false,
  transitionDemo,
}) => {
  const selectedPresetId = resolveMotionPresetId(animationSettings);
  const transition = animationSettings?.transition ?? null;
  const entrance = animationSettings?.screenAnimation ?? null;
  const paywallOverrideOn = !!animationSettings?.perKindOverrides?.Paywall;
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleSchemeSelect = useCallback(
    (id: string) => {
      onChange('animationSettings', expandMotionPreset(id as never));
    },
    [onChange]
  );

  const handlePaywallOverrideToggle = useCallback(
    (checked: boolean) => {
      onChange(
        'animationSettings.perKindOverrides',
        checked ? { Paywall: JSON.parse(JSON.stringify(THEME_PAYWALL_CALM_OVERRIDE)) } : null
      );
    },
    [onChange]
  );

  const handleTransitionTypeChange = useCallback(
    (value: string) => {
      if (!value) {
        onChange('animationSettings.transition', null);
        return;
      }
      if (transition) {
        onChange('animationSettings.transition.type', value);
      } else {
        onChange('animationSettings.transition', {
          type: value,
          timing: { type: 'tween', duration: 300, easing: 'easeInOutCubic' },
        });
      }
    },
    [onChange, transition]
  );

  const handleEntrancePresetChange = useCallback(
    (value: string) => {
      if (!value) {
        onChange('animationSettings.screenAnimation', null);
        return;
      }
      if (entrance) {
        onChange('animationSettings.screenAnimation.enterAnimation.preset', value);
      } else {
        onChange('animationSettings.screenAnimation', {
          enterAnimation: { preset: value, timing: { type: 'tween', duration: 450 } },
          stagger: { delayPerItem: 80 },
        });
      }
    },
    [onChange, entrance]
  );

  const hasSettings = !!animationSettings;

  return (
    <SAccordion
      title="Animations"
      collapsible
      defaultExpanded={defaultExpanded}
      disabled={disabled}
    >
      <Box data-testid="section-theme-animations" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {selectedPresetId === 'custom' && (
          <Box>
            <Chip size="small" label="Custom" data-testid="theme-anim-custom-chip" />
          </Box>
        )}

        {/* Scheme cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {THEME_MOTION_PRESETS.map((preset) => {
            const selected = selectedPresetId === preset.id;
            return (
              <ButtonBase
                key={preset.id}
                data-testid={`theme-anim-scheme-${preset.id}`}
                data-selected={selected ? 'true' : 'false'}
                onClick={() => handleSchemeSelect(preset.id)}
                disabled={disabled}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  boxShadow: selected ? (t) => `inset 0 0 0 1px ${t.palette.primary.main}` : 'none',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <span aria-hidden>{SCHEME_ICONS[preset.id]}</span>
                  {preset.label}
                  {preset.id === DEFAULT_THEME_MOTION_PRESET_ID && (
                    <Chip size="small" color="primary" label="Default" sx={{ height: 16, fontSize: 10 }} />
                  )}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  {preset.description}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {!hasSettings && (
          <Typography variant="caption" color="text.secondary">
            Pick a scheme to let the theme manage screen transitions and entrance animations.
          </Typography>
        )}

        {/* Paywall per-kind override */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={paywallOverrideOn}
              disabled={disabled || !hasSettings}
              onChange={(e) => handlePaywallOverrideToggle(e.target.checked)}
              data-testid="theme-anim-paywall-override"
            />
          }
          label={
            <Typography variant="body2">
              Calm Paywall{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                (coverVertical + fade — no bouncing prices)
              </Typography>
            </Typography>
          }
        />

        {/* Advanced — local disclosure, NOT an SAccordion: SAccordion forbids
            nesting (SAccordionNestingContext guard throws) and this section
            already lives inside the "Animations" SAccordion. */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <ButtonBase
            onClick={() => setAdvancedOpen((open) => !open)}
            disabled={disabled || !hasSettings}
            data-testid="theme-anim-advanced-toggle"
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              opacity: disabled || !hasSettings ? 0.5 : 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Advanced
            </Typography>
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: advancedOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms',
                color: 'text.secondary',
              }}
            />
          </ButtonBase>
          <Collapse in={advancedOpen && hasSettings}>
          <Box
            data-testid="theme-anim-advanced"
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 1.5, pb: 1.5, pt: 0.5 }}
          >
            <Typography variant="overline" color="text.secondary">
              Screen transition
            </Typography>
            <TextField
              select
              size="small"
              label="Type"
              value={transition?.type ?? ''}
              onChange={(e) => handleTransitionTypeChange(e.target.value)}
              disabled={disabled}
              slotProps={{ htmlInput: { 'data-testid': 'theme-anim-transition-type' } }}
            >
              <MenuItem value="">None</MenuItem>
              {THEME_TRANSITION_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Duration (ms)"
              value={transition?.timing?.duration ?? ''}
              disabled={disabled || !transition}
              onChange={(e) =>
                onChange(
                  'animationSettings.transition.timing.duration',
                  clamp(Number(e.target.value), TRANSITION_DURATION_MIN, TRANSITION_DURATION_MAX)
                )
              }
              slotProps={{
                htmlInput: {
                  min: TRANSITION_DURATION_MIN,
                  max: TRANSITION_DURATION_MAX,
                  'data-testid': 'theme-anim-transition-duration',
                },
              }}
            />
            {transition && THEME_DIRECTIONAL_TRANSITION_TYPES.has(transition.type as string) && (
              <TextField
                select
                size="small"
                label="Direction"
                value={transition.direction ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  // Default clears the key with undefined (cloneJson strips it),
                  // keeping the scheme-detection signature identical to the preset
                  onChange('animationSettings.transition.direction', e.target.value || undefined)}
                slotProps={{ htmlInput: { 'data-testid': 'theme-anim-transition-direction' } }}
              >
                <MenuItem value="">
                  {transition.type === 'coverVertical' ? 'Default (Up)' : 'Default (Left)'}
                </MenuItem>
                {THEME_TRANSITION_DIRECTION_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              size="small"
              label="Easing"
              value={transition?.timing?.easing ?? 'easeInOutCubic'}
              disabled={disabled || !transition}
              onChange={(e) => onChange('animationSettings.transition.timing.easing', e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'theme-anim-transition-easing' } }}
            >
              {THEME_EASING_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Delay (ms)"
              value={transition?.timing?.delay ?? ''}
              disabled={disabled || !transition}
              onChange={(e) =>
                onChange(
                  'animationSettings.transition.timing.delay',
                  clamp(Number(e.target.value), TRANSITION_DELAY_MIN, TRANSITION_DELAY_MAX)
                )
              }
              slotProps={{
                htmlInput: {
                  min: TRANSITION_DELAY_MIN,
                  max: TRANSITION_DELAY_MAX,
                  'data-testid': 'theme-anim-transition-delay',
                },
              }}
            />

            <Typography variant="overline" color="text.secondary">
              Screen entrance
            </Typography>
            <TextField
              select
              size="small"
              label="Preset"
              value={entrance?.enterAnimation?.preset ?? ''}
              onChange={(e) => handleEntrancePresetChange(e.target.value)}
              disabled={disabled}
              slotProps={{ htmlInput: { 'data-testid': 'theme-anim-entrance-preset' } }}
            >
              <MenuItem value="">None</MenuItem>
              {V2_ENTRANCE_PRESET_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Duration (ms)"
              value={entrance?.enterAnimation?.timing?.duration ?? ''}
              disabled={disabled || !entrance}
              onChange={(e) =>
                onChange(
                  'animationSettings.screenAnimation.enterAnimation.timing.duration',
                  clamp(Number(e.target.value), TRANSITION_DURATION_MIN, TRANSITION_DURATION_MAX)
                )
              }
              slotProps={{
                htmlInput: {
                  min: TRANSITION_DURATION_MIN,
                  max: TRANSITION_DURATION_MAX,
                  'data-testid': 'theme-anim-entrance-duration',
                },
              }}
            />
            <TextField
              size="small"
              type="number"
              label="Stagger (ms / element)"
              value={entrance?.stagger?.delayPerItem ?? ''}
              disabled={disabled || !entrance}
              onChange={(e) =>
                onChange(
                  'animationSettings.screenAnimation.stagger.delayPerItem',
                  clamp(Number(e.target.value), STAGGER_MIN, STAGGER_MAX)
                )
              }
              slotProps={{
                htmlInput: { min: STAGGER_MIN, max: STAGGER_MAX, 'data-testid': 'theme-anim-entrance-stagger' },
              }}
            />
          </Box>
          </Collapse>
        </Box>

        {transitionDemo}
      </Box>
    </SAccordion>
  );
};

export default V2AnimationsSection;
